import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { fundingColors as C } from '../../theme/fundingColors'

/**
 * Height of the hover-revealed row actions, and the space the header reserves for
 * them. Both come from here: the buttons appear on hover via `display: none` →
 * visible, which would otherwise grow the row from 16px to 26px and nudge the
 * whole card down as the pointer moves across it.
 */
const HOVER_ACTION_HEIGHT = 26

export const useStyles = makeStyles()((theme: Theme) => ({
  card: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 0,
    backgroundColor: C.cardBg,
    borderRadius: 8,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  headerIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 12, color: C.darkText },
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: C.darkText,
    margin: 0,
  },
  infoIcon: {
    fontSize: 12,
    color: C.mutedText,
    marginLeft: 8,
  },
  learnMore: {
    padding: 0,
    border: 'none',
    background: 'none',
    font: 'inherit',
    cursor: 'pointer',
    color: C.chartSeriesExtra,
    fontWeight: 500,
    letterSpacing: '0.5px',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '& svg': {
      fontSize: 11,
      marginLeft: 4,
    },
  },
  scrollWrap: {
    position: 'relative',
    width: '100%',
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
  },
  programs: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    flex: '1 1 auto',
    minHeight: 0,
    maxHeight: 280,
    overflowY: 'auto',
  },
  program: {
    boxSizing: 'border-box',
    backgroundColor: C.metricBg,
    borderRadius: 8,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    '&:hover .walletHoverAction, &:focus-within .walletHoverAction': {
      display: 'inline-flex',
    },
    '&:hover .walletReviewBtn, &:focus-within .walletReviewBtn': {
      display: 'flex',
    },
    '&:hover .walletWarnText, &:focus-within .walletWarnText': {
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  },
  programHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
  },
  programName: {
    flex: '1 1 auto',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: C.darkText,
    margin: 0,
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Fixed-width right group (Figma node 367:47711): the date fills it and sits
  // flush right; on hover the "View history" button appears inside the same
  // width so the title never reflows.
  headerActions: {
    width: 171,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    // Held open whether or not the buttons are showing, so hovering a row does
    // not change its height.
    minHeight: HOVER_ACTION_HEIGHT,
  },
  lastUpdated: {
    flex: '1 1 auto',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: C.mutedText,
    margin: 0,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  viewHistoryBtn: {
    display: 'none',
    height: HOVER_ACTION_HEIGHT,
    flexShrink: 0,
    boxSizing: 'border-box',
    backgroundColor: C.cardBg,
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 8,
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '0.5px',
    color: C.linkBlue,
    whiteSpace: 'nowrap',
  },
  warning: {
    boxSizing: 'border-box',
    backgroundColor: C.warningBg,
    borderRadius: 4,
    padding: '4px 8px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  warningText: {
    flex: '1 1 auto',
    minWidth: 0,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: C.darkText,
    margin: 0,
    '& b': { fontWeight: 500 },
  },
  reviewBtn: {
    flexShrink: 0,
    height: HOVER_ACTION_HEIGHT,
    boxSizing: 'border-box',
    display: 'none',
    alignItems: 'center',
    gap: 4,
    backgroundColor: C.cardBg,
    border: `1px solid ${C.reviewBorder}`,
    borderRadius: 8,
    padding: '4px 8px',
    cursor: 'pointer',
  },
  reviewIcon: {
    width: 14,
    height: 14,
  },
  reviewText: {
    fontSize: 11,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '0.5px',
    color: C.darkText,
    whiteSpace: 'nowrap',
  },
  // The shared loader is sized for full-page use with fixed-px internals we
  // can't resize from here, so scale the whole subtree down to fit this card.
  loading: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    '& > *': {
      transform: 'scale(0.6)',
      transformOrigin: 'center',
    },
  },
}))
